/**
 * rPI Porter — Auto-reply rule API client (E-102 P3-1)
 *
 * Wraps:
 *  GET    /api/v1/auto-reply       — list rules
 *  GET    /api/v1/auto-reply/:id   — rule detail
 *  POST   /api/v1/auto-reply       — create rule
 *  PATCH  /api/v1/auto-reply/:id   — update rule
 *  DELETE /api/v1/auto-reply/:id   — soft-delete rule
 *
 * OWNER-only endpoints (JWT role guard on backend).
 */

import { api } from './client';

// ============================================
// TYPES — mirror backend AutoReplyRule + DTOs
// ============================================

export type AutoReplyTrigger =
  | 'NEW_CONVERSATION'
  | 'OFF_HOURS'
  | 'KEYWORD'
  | 'NO_RESPONSE_TIMER'
  | 'CHANNEL_SPECIFIC';

export interface AutoReplyRule {
  id: string;
  tenantId: string;
  name: string;
  trigger: AutoReplyTrigger;
  conditionJson: Record<string, unknown>;
  templateId: string;
  active: boolean;
  chatwootAutomationId: number | null;
  lastSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAutoReplyInput {
  name: string;
  trigger: AutoReplyTrigger;
  conditionJson?: Record<string, unknown> | undefined;
  templateId: string;
  active?: boolean | undefined;
}

export interface UpdateAutoReplyInput {
  name?: string | undefined;
  trigger?: AutoReplyTrigger | undefined;
  conditionJson?: Record<string, unknown> | undefined;
  templateId?: string | undefined;
  active?: boolean | undefined;
}

// ============================================
// API FUNCTIONS
// ============================================

export async function listAutoReplyRules(): Promise<AutoReplyRule[]> {
  const res = await api.get<{ data: AutoReplyRule[] }>('/auto-reply');
  return res.data;
}

export async function getAutoReplyRule(id: string): Promise<AutoReplyRule> {
  const res = await api.get<{ data: AutoReplyRule }>(`/auto-reply/${id}`);
  return res.data;
}

export async function createAutoReplyRule(input: CreateAutoReplyInput): Promise<AutoReplyRule> {
  const res = await api.post<{ data: AutoReplyRule }>('/auto-reply', input);
  return res.data;
}

export async function updateAutoReplyRule(
  id: string,
  input: UpdateAutoReplyInput
): Promise<AutoReplyRule> {
  const res = await api.patch<{ data: AutoReplyRule }>(`/auto-reply/${id}`, input);
  return res.data;
}

export async function deleteAutoReplyRule(id: string): Promise<void> {
  await api.delete(`/auto-reply/${id}`);
}
